import { useDeferredValue, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { Station } from "../lib/m3u";
import { searchStations, toSearchable } from "../lib/search";
import Pagination from "./Pagination";
import SearchBox from "./SearchBox";
import StationRow from "./StationRow";

const PAGE_SIZE = 50;

type Props = {
  stations: Station[];
  emptyMessage: string;
};

export default function StationList({ stations, emptyMessage }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const query = searchParams.get("q") ?? "";
  const deferredQuery = useDeferredValue(query);

  const searchable = useMemo(() => toSearchable(stations), [stations]);
  const results = useMemo(() => searchStations(searchable, deferredQuery), [searchable, deferredQuery]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [requestedPage]);

  const setQuery = (value: string) => {
    setSearchParams(
      (params) => {
        if (value) params.set("q", value);
        else params.delete("q");
        params.delete("page");
        return params;
      },
      { replace: true }
    );
  };

  if (stations.length === 0) return <p className="text-slate-600">{emptyMessage}</p>;

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const page = Math.min(Math.max(Number.isNaN(requestedPage) ? 1 : requestedPage, 1), totalPages);
  const pageStations = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total = stations.length.toLocaleString();

  return (
    <div className="space-y-4">
      <SearchBox value={query} onChange={setQuery} />
      {results.length === 0 ? (
        <p className="text-sm text-slate-600">
          No stations match “{deferredQuery}” (searched {total} stations).
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            Showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + pageStations.length} of{" "}
            {results.length.toLocaleString()} {deferredQuery ? `matching stations (out of ${total})` : "stations"}
          </p>
          <Pagination page={page} totalPages={totalPages} />
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {pageStations.map((station) => (
              <StationRow key={station.id} station={station} />
            ))}
          </ul>
          <Pagination page={page} totalPages={totalPages} />
        </>
      )}
    </div>
  );
}
